import React, { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/data";
import { useAuth } from "../../contexts/AuthContext";
import MemberNav from "./MemberNav";
import MembershipCard from "./MembershipCard";
import { logError } from "../../utils/logger";
import { parseLambdaResponse } from "../../utils/parseLambdaResponse";

const client = generateClient({ authMode: 'userPool' });

interface MemberProfileProps {
  onNavigate: (page: "dashboard" | "profile" | "competitions" | "catches" | "my-stats" | "my-fishing" | "season-championship" | "home") => void;
}

const MemberProfile: React.FC<MemberProfileProps> = ({ onNavigate }) => {
  const { userId } = useAuth();

  const [member, setMember] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    phone: "",
    address: "",
    suburb: "",
    state: "",
    postCode: "",
    emergencyContact: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Photo upload state
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [loadedPhotoUrl, setLoadedPhotoUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Fetch member data on mount
  useEffect(() => {
    fetchMemberData();
  }, [userId]);

  const fetchMemberData = async () => {
    if (!userId) {
      setLoadError("User not authenticated");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setLoadError(null);

      // Own profile, via the narrow server-side projection (replaces direct
      // Member.list({filter: {cognitoSub}}), which relied on the model-level
      // allow.authenticated() grant exposing every field on every member's
      // record to any signed-in user, not just the caller's own).
      const profileResult: any = await (client.queries as any).getOwnMemberProfile({});
      const profileResponse = parseLambdaResponse(profileResult?.data);

      if (!profileResponse?.success) {
        logError("MemberProfile", [{ message: profileResponse?.error || "Unknown error" }]);
        setLoadError(profileResponse?.error || "Failed to load profile data");
        setIsLoading(false);
        return;
      }

      const memberData = profileResponse.own;
      if (!memberData) {
        setLoadError("Member profile not found");
        setIsLoading(false);
        return;
      }

      setMember(memberData);

      // Load existing profile photo via presigned URL
      if (memberData.profilePhotoS3Key) {
        try {
          const { data: urlData } = await (client.queries as any).generateDownloadUrl({
            s3Key: memberData.profilePhotoS3Key,
          });
          const parsed = typeof urlData === 'string' ? JSON.parse(urlData) : urlData;
          if (parsed?.downloadUrl) setLoadedPhotoUrl(parsed.downloadUrl);
        } catch {
          // Non-fatal — photo just won't display
        }
      } else {
        setLoadedPhotoUrl(null);
      }

      // Initialize form data
      setFormData({
        phone: memberData.phoneNumber || "",
        address: memberData.address || "",
        suburb: memberData.suburb || "",
        state: memberData.state || "",
        postCode: memberData.postCode || "",
        emergencyContact:
          memberData.emergencyContactName && memberData.emergencyContactPhone
            ? `${memberData.emergencyContactName} - ${memberData.emergencyContactPhone}`
            : "",
      });

      setIsLoading(false);
    } catch (error) {
      logError("MemberProfile", error);
      setLoadError("An error occurred while loading your profile");
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!member) return;

    setIsSaving(true);
    setSaveMessage(null);

    // Validation
    if (!formData.phone) {
      setSaveMessage({ type: "error", text: "Phone number is required" });
      setIsSaving(false);
      return;
    }

    try {
      // Parse emergency contact
      let emergencyContactName = "";
      let emergencyContactPhone = "";
      if (formData.emergencyContact) {
        const parts = formData.emergencyContact.split("-").map((p) => p.trim());
        emergencyContactName = parts[0] || "";
        emergencyContactPhone = parts[1] || "";
      }

      // SEC-04: self-edit goes through updateFamilyMember (Lambda), not a
      // direct client.models.Member.update — the Member model grants no
      // update to members at all any more. canEditFamilyMember already
      // allows the caller-is-target case. '' clears an optional field.
      const result: any = await (client.mutations as any).updateFamilyMember(
        {
          targetMemberId: member.id,
          phoneNumber: formData.phone,
          address: formData.address || "",
          suburb: formData.suburb || "",
          state: formData.state || "",
          postCode: formData.postCode || "",
          emergencyContactName: emergencyContactName || "",
          emergencyContactPhone: emergencyContactPhone || "",
        },
        { authMode: 'userPool' },
      );

      const response = parseLambdaResponse(result?.data);

      if (!response?.success) {
        logError("MemberProfile", response?.error || result?.errors);
        setSaveMessage({ type: "error", text: "Failed to update profile" });
        setIsSaving(false);
        return;
      }

      // Refresh member data
      await fetchMemberData();

      setSaveMessage({
        type: "success",
        text: "Profile updated successfully!",
      });
      setIsEditing(false);

      // Clear message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      logError("MemberProfile", error);
      setSaveMessage({
        type: "error",
        text: "An error occurred while updating your profile",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!member) return;

    setFormData({
      phone: member.phoneNumber || "",
      address: member.address || "",
      suburb: member.suburb || "",
      state: member.state || "",
      postCode: member.postCode || "",
      emergencyContact:
        member.emergencyContactName && member.emergencyContactPhone
          ? `${member.emergencyContactName} - ${member.emergencyContactPhone}`
          : "",
    });
    setIsEditing(false);
    setSaveMessage(null);
  };

  const handlePhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !userId) return;

    setUploadError(null);

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setUploadError(
        "Invalid file type. Please upload a JPEG, PNG, or WebP image.",
      );
      return;
    }

    // Validate file size (max 10MB before compression)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError("File too large. Maximum size is 10MB.");
      return;
    }

    try {
      setIsUploadingPhoto(true);

      // Compress image client-side
      const imageCompression = (await import('browser-image-compression')).default;
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
      });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(compressed);

      // Step 1: Get presigned upload URL
      const uploadContentType = compressed.type || file.type || 'image/jpeg';
      const { data: urlData, errors: urlErrors } =
        await (client.queries as any).generateUploadUrl({
          purpose: "PROFILE_PHOTO",
          fileName: file.name,
          contentType: uploadContentType,
          identityId: userId,
        });

      if (urlErrors || !urlData) {
        logError("MemberProfile", urlErrors);
        setUploadError("Failed to prepare upload. Please try again.");
        setIsUploadingPhoto(false);
        return;
      }

      const parsed = typeof urlData === 'string' ? JSON.parse(urlData) : urlData;
      const { uploadUrl, s3Key } = parsed as {
        uploadUrl: string;
        s3Key: string;
        expiresIn: number;
      };

      // Step 2: Upload file to S3 using presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: compressed,
        headers: {
          "Content-Type": uploadContentType,
        },
      });

      if (!uploadResponse.ok) {
        const errText = await uploadResponse.text().catch(() => '');
        logError("MemberProfile", `S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText} ${errText}`);
        setUploadError(`Failed to upload photo (${uploadResponse.status}). Please try again.`);
        setIsUploadingPhoto(false);
        return;
      }

      // Step 3: Update Member record with S3 key — SEC-04: through
      // updateFamilyMember (Lambda), not a direct Member.update. The
      // handler only accepts profilePhotoS3Key on self-edit, and only when
      // it exactly matches the key shape generate-upload-url just produced
      // for this caller (see update-family-member/rules.ts).
      const updateResult: any = await (client.mutations as any).updateFamilyMember(
        {
          targetMemberId: member.id,
          profilePhotoS3Key: s3Key,
        },
        { authMode: 'userPool' },
      );

      const updateResponse = parseLambdaResponse(updateResult?.data);

      if (!updateResponse?.success) {
        logError("MemberProfile", updateResponse?.error || updateResult?.errors);
        setUploadError(
          "Photo uploaded but failed to save to your profile. This can happen if your account wasn't linked correctly — please contact an admin.",
        );
        setIsUploadingPhoto(false);
        return;
      }

      // Refresh member data to get the new photo
      await fetchMemberData();

      setSaveMessage({
        type: "success",
        text: "Profile photo updated successfully!",
      });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error: any) {
      logError("MemberProfile", error);
      setUploadError(error?.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage="profile" onNavigate={onNavigate} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError || !member) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage="profile" onNavigate={onNavigate} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">
              {loadError || "Failed to load profile"}
            </p>
            <button
              onClick={fetchMemberData}
              className="mt-4 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MemberNav currentPage="profile" onNavigate={onNavigate} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="mt-2 text-gray-600">
            Manage your personal information and membership details
          </p>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`mb-6 p-4 rounded-md ${
              saveMessage.type === "success"
                ? "bg-green-50 border border-green-200"
                : "bg-red-50 border border-red-200"
            }`}
          >
            <p
              className={`text-sm font-medium ${
                saveMessage.type === "success"
                  ? "text-green-800"
                  : "text-red-800"
              }`}
            >
              {saveMessage.text}
            </p>
          </div>
        )}

        {/* Digital Membership Card — shown first */}
        <div className="mb-8">
          <MembershipCard
            member={{
              firstName: member.firstName,
              lastName: member.lastName,
              memberNumber: member.memberNumber || "Pending",
              membershipType: member.membershipType,
              status: member.status,
              profilePhotoS3Key: member.profilePhotoS3Key,
              email: member.email,
              membershipYear: member.membershipYear,
              membershipExpiryDate: member.membershipExpiryDate,
              renewalStatus: member.renewalStatus,
              isCommittee: member.isCommittee,
            }}
            profilePhotoUrl={photoPreview || loadedPhotoUrl || undefined}
          />
        </div>

        {/* Profile Photo Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Profile Photo
          </h2>

          {uploadError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{uploadError}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:space-x-6">
            <div className="flex-shrink-0">
              {photoPreview || loadedPhotoUrl ? (
                <img
                  src={photoPreview || loadedPhotoUrl || ''}
                  alt="Profile"
                  className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-3xl sm:text-4xl text-gray-400">👤</span>
                </div>
              )}
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="text-sm text-gray-600 mb-2">
                Upload a profile photo (JPEG, PNG, or WebP, max 10MB)
              </p>
              <input
                type="file"
                id="photoUpload"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoUpload}
                disabled={isUploadingPhoto}
                className="hidden"
              />
              <label
                htmlFor="photoUpload"
                className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  isUploadingPhoto
                    ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                    : "text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                }`}
              >
                {isUploadingPhoto ? "Uploading..." : "Upload Photo"}
              </label>
            </div>
          </div>
        </div>

        {/* Editable Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Contact Information
            </h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
              >
                Edit
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue ${
                  !isEditing ? "bg-gray-50 text-gray-600" : ""
                }`}
                placeholder="0412 345 678"
              />
            </div>

            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Street Address
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue ${
                  !isEditing ? "bg-gray-50 text-gray-600" : ""
                }`}
                placeholder="123 Main St"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="suburb"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Suburb
                </label>
                <input
                  type="text"
                  id="suburb"
                  name="suburb"
                  value={formData.suburb}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue ${
                    !isEditing ? "bg-gray-50 text-gray-600" : ""
                  }`}
                  placeholder="Tumbulgum"
                />
              </div>

              <div>
                <label
                  htmlFor="state"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue ${
                    !isEditing ? "bg-gray-50 text-gray-600" : ""
                  }`}
                  placeholder="NSW"
                />
              </div>

              <div>
                <label
                  htmlFor="postCode"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Post Code
                </label>
                <input
                  type="text"
                  id="postCode"
                  name="postCode"
                  value={formData.postCode}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  maxLength={4}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue ${
                    !isEditing ? "bg-gray-50 text-gray-600" : ""
                  }`}
                  placeholder="2490"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="emergencyContact"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Emergency Contact
              </label>
              <input
                type="text"
                id="emergencyContact"
                name="emergencyContact"
                value={formData.emergencyContact}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue ${
                  !isEditing ? "bg-gray-50 text-gray-600" : ""
                }`}
                placeholder="Name - Phone"
              />
            </div>
          </div>

          {isEditing && (
            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:space-x-3">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          )}
        </div>


        {/* Read-Only Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Membership Details
            <span className="ml-2 text-sm font-normal text-gray-500">
              (Admin Only)
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="text-base font-medium text-gray-900">
                {member.firstName} {member.lastName}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-base font-medium text-gray-900">
                {member.email}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Member Number</p>
              <p className="text-base font-medium text-gray-900">
                {member.memberNumber || "Not assigned"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Membership Type</p>
              <p className="text-base font-medium text-gray-900">
                {member.membershipType?.replace(/_/g, " ")}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Membership Status</p>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  member.isActive
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {member.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-500">Payment Status</p>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  member.paymentReceived
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {member.paymentReceived ? "Paid" : "Pending"}
              </span>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> To update your name, email, membership
              type, or payment information, please contact the club
              administrator.
            </p>
          </div>
        </div>

        {/* Family Members */}
        {member.membershipType === 'FAMILY' && member.familyMembers && (() => {
          try {
            const familyData: { firstName: string; lastName: string; memberNumber?: string }[] =
              typeof member.familyMembers === 'string'
                ? JSON.parse(member.familyMembers)
                : Array.isArray(member.familyMembers)
                  ? member.familyMembers
                  : [];
            if (!Array.isArray(familyData) || familyData.length === 0) return null;
            return (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Family Members
                </h2>
                <div className="divide-y divide-gray-200">
                  {familyData.map((fm: { firstName: string; lastName: string; memberNumber?: string }, idx: number) => (
                    <div key={idx} className="flex items-center justify-between py-3">
                      <span className="text-base font-medium text-gray-900">
                        {fm.firstName} {fm.lastName}
                      </span>
                      <span className={`text-sm font-medium ${fm.memberNumber ? 'text-gray-900' : 'text-yellow-600'}`}>
                        {fm.memberNumber || 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          } catch {
            return null;
          }
        })()}

      </div>
    </div>
  );
};

export default MemberProfile;
